import { useEffect, useState } from "react";
import { ClipboardList, Download, X } from "lucide-react";
import Button from "../ui/Button";
import {
  PROVIDER_ONBOARDING_FIELDS,
  downloadInstitutionOnboardingTemplate,
} from "../../data/institutionOnboardingGuide";

export default function OnboardingRequirementsButton({ className = "" }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <Button variant="outline" size="sm" className={className} onClick={() => setOpen(true)}>
        <ClipboardList className="h-4 w-4" />
        Fields to collect
      </Button>

      {open ? (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-primary/40 p-4 sm:items-center">
          <button
            type="button"
            className="absolute inset-0"
            aria-label="Close"
            onClick={() => setOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="onboarding-fields-title"
            className="relative flex max-h-[min(40rem,calc(100vh-2rem))] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
          >
            <div className="flex items-start justify-between gap-3 border-b border-primary/[0.06] px-5 py-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
                  Before you onboard
                </p>
                <h2 id="onboarding-fields-title" className="mt-1 text-base font-bold text-primary">
                  Ask the provider for these fields
                </h2>
                <p className="mt-1 text-xs leading-relaxed text-primary/55">
                  These are enough to open the institution, set the SLA days, and send the portal login.
                  City, address, website, logo, a second contact, and social links can be added after they
                  sign in.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-1 text-primary/40 hover:bg-primary/5"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto px-5 py-4">
              {PROVIDER_ONBOARDING_FIELDS.map((field) => (
                <li key={field.label} className="rounded-xl border border-primary/[0.07] px-3 py-2.5">
                  <p className="text-sm font-bold text-primary">{field.label}</p>
                  <p className="mt-0.5 text-xs text-primary/60">{field.ask}</p>
                  <p className="mt-1 text-[11px] text-primary/45">
                    Example: {field.example}. {field.format}
                  </p>
                </li>
              ))}
              <li className="rounded-xl bg-primary/[0.03] px-3 py-2.5 text-xs leading-relaxed text-primary/55">
                Collect the total SLA days here. On the onboard form, you decide how many of those days
                the call centre manager has to assign cases, and how many the agent has to make first
                contact.
              </li>
            </ul>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-primary/[0.06] px-5 py-3">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                Close
              </Button>
              <Button variant="accent" size="sm" onClick={downloadInstitutionOnboardingTemplate}>
                <Download className="h-4 w-4" />
                Download Excel template
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
